/**
 * Proposals Page
 * BuilderBel Construction Project Management System
 *
 * Lists all proposals for the current project with filtering, stats,
 * workflow actions (send, revise, delete), and PDF download.
 */

import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { DatePicker, SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import ProposalDrawer from '../components/proposals/ProposalDrawer';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeleteProposalMutation,
  useGetProposalStatsQuery,
  useGetProposalsQuery,
  useReviseProposalMutation,
  useSendProposalMutation,
} from '../services/proposalApi';
import type {
  Proposal,
  ProposalListParams,
  ProposalStatus,
} from '../types/proposal';

// ============================================================================
// Constants
// ============================================================================

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'revised', label: 'Revised' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'proposalDate', label: 'Proposal Date' },
  { value: 'validUntil', label: 'Valid Until' },
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'totalAmount', label: 'Total Amount' },
  { value: 'proposalNumber', label: 'Proposal Number' },
];

const PAGE_SIZE = 20;

// ============================================================================
// Helpers
// ============================================================================

const getStatusVariant = (
  status: ProposalStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'accepted':
      return 'success';
    case 'sent':
      return 'info';
    case 'viewed':
      return 'purple';
    case 'rejected':
      return 'error';
    case 'expired':
      return 'warning';
    case 'revised':
      return 'neutral';
    case 'draft':
    default:
      return 'neutral';
  }
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// ============================================================================
// Main Component
// ============================================================================

const Proposals: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  // Build query params
  const queryParams = useMemo<ProposalListParams>(() => {
    const params: ProposalListParams = {
      page: currentPage,
      limit: PAGE_SIZE,
      sortBy: sortBy as ProposalListParams['sortBy'],
      sortOrder,
    };
    if (statusFilter) params.status = statusFilter as ProposalStatus;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [currentPage, sortBy, sortOrder, statusFilter, dateFrom, dateTo]);

  // RTK Query
  const { data: proposalsData, isLoading: isLoadingProposals } = useGetProposalsQuery(
    { projectId: projectId || '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetProposalStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteProposal, { isLoading: isDeleting }] = useDeleteProposalMutation();
  const [sendProposal, { isLoading: isSending }] = useSendProposalMutation();
  const [reviseProposal, { isLoading: isRevising }] = useReviseProposalMutation();

  const proposals = proposalsData?.data?.proposals ?? [];
  const pagination = proposalsData?.data?.pagination;
  const stats = statsData?.data;

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleOpenCreate = useCallback(() => {
    setSelectedProposal(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((proposal: Proposal) => {
    setSelectedProposal(proposal);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedProposal(null);
  }, []);

  const handleDelete = useCallback(
    async (proposal: Proposal) => {
      if (!confirm(`Delete proposal "${proposal.name}"? This cannot be undone.`)) return;
      try {
        await deleteProposal({ projectId: projectId || '', proposalId: proposal._id }).unwrap();
        toast.success('Proposal deleted');
      } catch {
        toast.error('Failed to delete proposal');
      }
    },
    [deleteProposal, projectId, toast]
  );

  const handleSend = useCallback(
    async (proposal: Proposal) => {
      if (!confirm(`Mark proposal "${proposal.name}" as sent?`)) return;
      try {
        await sendProposal({ projectId: projectId || '', proposalId: proposal._id }).unwrap();
        toast.success('Proposal marked as sent');
      } catch {
        toast.error('Failed to send proposal');
      }
    },
    [sendProposal, projectId, toast]
  );

  const handleRevise = useCallback(
    async (proposal: Proposal) => {
      if (!confirm(`Create a new revision of "${proposal.name}"? The original will be marked as revised.`)) return;
      try {
        await reviseProposal({ projectId: projectId || '', proposalId: proposal._id }).unwrap();
        toast.success('Revision created as new draft');
      } catch {
        toast.error('Failed to create revision');
      }
    },
    [reviseProposal, projectId, toast]
  );

  const handleDownloadPDF = useCallback(
    async (proposal: Proposal) => {
      try {
        const token = localStorage.getItem('auth_token');
        const url = `/api/projects/${projectId}/proposals/${proposal._id}/pdf`;
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          toast.error('Failed to generate PDF');
          return;
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `proposal-${proposal.proposalNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // ============================================================================
  // Action menu builder
  // ============================================================================

  const getActions = useCallback(
    (proposal: Proposal): ActionMenuItem[] => {
      const isDraft = proposal.status === 'draft';
      const isAccepted = proposal.status === 'accepted';
      const isRevised = proposal.status === 'revised';

      const actions: ActionMenuItem[] = [
        {
          label: 'View / Edit',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleOpenEdit(proposal),
        },
      ];

      if (isDraft) {
        actions.push({
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleOpenEdit(proposal),
        });
        actions.push({
          label: 'Send',
          icon: <Send className="h-4 w-4" />,
          onClick: () => handleSend(proposal),
        });
      }

      if (!isDraft && !isRevised) {
        actions.push({
          label: 'Create Revision',
          icon: <RefreshCw className="h-4 w-4" />,
          onClick: () => handleRevise(proposal),
        });
      }

      actions.push({
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPDF(proposal),
      });

      if (isDraft && !isAccepted) {
        actions.push({
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(proposal),
          variant: 'danger',
        });
      }

      return actions;
    },
    [handleOpenEdit, handleSend, handleRevise, handleDownloadPDF, handleDelete]
  );

  // ============================================================================
  // Stats cards
  // ============================================================================

  const moduleStats = useMemo(() => {
    if (!stats) return [];
    return [
      { title: 'Total Proposals', value: stats.total },
      { title: 'Accepted', value: stats.accepted },
      { title: 'Sent / Viewed', value: stats.sent + (stats.byStatus['viewed'] ?? 0) },
      { title: 'Draft', value: stats.draft },
    ];
  }, [stats]);

  // ============================================================================
  // Render
  // ============================================================================

  const isActionLoading = isDeleting || isSending || isRevising;

  return (
    <ModuleLayout
      title="Proposals"
      description="Create and manage construction proposals for clients"
      icon={<FileText className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Create Proposal',
        onClick: handleOpenCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={moduleStats}
    >
      {/* ======================================================================
          FILTERS
      ====================================================================== */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-40">
          <SearchableDropdown
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={val => {
              setStatusFilter(val);
              setCurrentPage(1);
            }}
            placeholder="All Status"
          />
        </div>
        <div className="w-36">
          <DatePicker
            value={dateFrom}
            onChange={val => {
              setDateFrom(val);
              setCurrentPage(1);
            }}
            placeholder="Date from"
          />
        </div>
        <div className="w-36">
          <DatePicker
            value={dateTo}
            onChange={val => {
              setDateTo(val);
              setCurrentPage(1);
            }}
            placeholder="Date to"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={val => {
              setSortBy(val);
              setCurrentPage(1);
            }}
            placeholder="Sort by"
          />
        </div>
      </div>

      {/* ======================================================================
          TABLE
      ====================================================================== */}
      {isLoadingProposals ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : proposals.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No proposals found"
          description="Create professional proposals for your clients, track their status, and manage revisions."
          action={{
            label: 'Create Proposal',
            onClick: handleOpenCreate,
          }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proposal
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proposal Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid Until
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {proposals.map(proposal => (
                  <tr
                    key={proposal._id}
                    className={`hover:bg-gray-50 transition-colors ${isActionLoading ? 'opacity-60' : ''}`}
                  >
                    {/* Proposal name + number */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {proposal.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{proposal.proposalNumber}</p>
                      </div>
                    </td>

                    {/* Client */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-gray-700">{proposal.clientContact.name}</p>
                        <p className="text-xs text-gray-400">{proposal.clientContact.email}</p>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={proposal.status}
                        variant={getStatusVariant(proposal.status)}
                      />
                    </td>

                    {/* Total */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(proposal.totalAmount, proposal.currency)}
                      </span>
                    </td>

                    {/* Proposal date */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {formatDate(proposal.proposalDate)}
                      </span>
                    </td>

                    {/* Valid until */}
                    <td className="px-4 py-3">
                      <span
                        className={`text-sm ${
                          proposal.status === 'sent' || proposal.status === 'viewed'
                            ? new Date(proposal.validUntil) < new Date()
                              ? 'text-red-600 font-medium'
                              : 'text-gray-600'
                            : 'text-gray-600'
                        }`}
                      >
                        {formatDate(proposal.validUntil)}
                      </span>
                    </td>

                    {/* Version */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">v{proposal.version}</span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <ActionMenu
                        items={getActions(proposal)}
                        id={`action-menu-${proposal._id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} proposals
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!pagination.hasPrev}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-600 px-2">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================================================================
          DRAWER
      ====================================================================== */}
      <ProposalDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        projectId={projectId || ''}
        proposal={selectedProposal}
        mode={drawerMode}
      />
    </ModuleLayout>
  );
};

export default Proposals;
